// Copyright 2025, University of Maryland, All Rights Reserved
import { useState } from 'react';
import { useAuth } from '@umd-dit-ai-workspace/workspaces-component';
import { TemplatesList } from './TemplatesList';
import { TemplateEditor } from './TemplateEditor';
import { useConfigMutations } from '../hooks/useConfigMutations';
import type {
  TemplateResponse,
  PromptResponse,
  ConfigurationLevel,
} from '../types/config';

interface TemplatesConfigurationViewProps {
  level: ConfigurationLevel;
}

export function TemplatesConfigurationView({
  level,
}: TemplatesConfigurationViewProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateResponse | null>(null);

  const { selectedOrganizationId, selectedWorkspaceId } = useAuth();

  const mutations = useConfigMutations({
    level,
    orgId: level === 'workspace' ? selectedOrganizationId || undefined : undefined,
    wsId: level === 'workspace' ? selectedWorkspaceId || undefined : undefined,
  });

  const handleTemplateFetched = (data: TemplateResponse | PromptResponse) => {
    setSelectedTemplate(data as TemplateResponse);
  };

  const handleSelectTemplate = (template: string) => {
    mutations.getTemplate.mutate(
      { name: template },
      { onSuccess: handleTemplateFetched }
    );
  };

  const handleTemplateChange = (template: TemplateResponse | null) => {
    setSelectedTemplate(template);
  };

  // Handle successful template creation
  const handleTemplateCreated = (name: string) => {
    // Fetch the newly created template to get its full data
    handleSelectTemplate(name);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <TemplatesList
        level={level}
        selectedTemplate={selectedTemplate}
        onSelectTemplate={handleSelectTemplate}
      />

      <TemplateEditor
        level={level}
        selectedTemplate={selectedTemplate}
        onTemplateChange={handleTemplateChange}
        onTemplateCreated={handleTemplateCreated}
      />
    </div>
  );
}
